import type { TransactionData } from "@/types/types";
import type { CoverageInfo } from "@/types/payments";
import type {
  CoverageState,
  SubscriptionPaymentSummary,
} from "@/store/api/paymentSummaryStore";

/**
 * Calculs d'affichage de l'écran « Gestion des paiements », extraits des
 * composants (PaymentsMainPanel, TransactionList) pour être testables.
 * Les corps sont VOLONTAIREMENT identiques au code d'origine : toute
 * évolution du comportement doit passer par les tests de
 * `__tests__/paymentsView.test.ts`.
 */

export type QuickFilter = "" | "late" | "pending" | CoverageState;

export interface PaymentKpis {
  unpaid: number;
  partial: number;
  paid: number;
  overpaid: number;
  pending: number;
}

/**
 * Montants encaissé / restant d'une ligne.
 *
 * Ils étaient reconstruits depuis `coveragePct`, un pourcentage **entier**
 * (arrondi côté SQL puis re-arrondi côté client) : 100 payés sur 300
 * s'affichaient 99,00 et « Restant 201,00 ». `totalPaid` est la somme exacte
 * des paiements encaissés, disponible dans la même structure — on l'utilise.
 */
export const paidAndLeft = (tx: TransactionData, cov?: CoverageInfo) => {
  const price = Number(cov?.price ?? tx.amount ?? 0);
  const paid = Number(cov?.totalPaid ?? 0);
  return { paid, left: price - paid };
};

/** KPIs des cartes du haut (ignorer price=0 && totalPaid=0). */
export const computeKpis = (summaries: SubscriptionPaymentSummary[]): PaymentKpis => {
  const counts = { unpaid: 0, partial: 0, paid: 0, overpaid: 0, pending: 0 };
  for (const s of summaries) {
    if (s.subscriptionPrice === 0 && s.totalPaid === 0) continue;
    if (s.coverageState !== "ignored") (counts as any)[s.coverageState] += 1;
    // « En attente » se superpose aux autres états : c'est un compte à part,
    // pas une catégorie exclusive.
    if (s.pendingCount > 0) counts.pending += 1;
  }
  return counts;
};

/** Index de couverture par abonnement (badges/infos de la liste). */
export const buildCoverageBySub = (
  summaries: SubscriptionPaymentSummary[]
): Record<string, CoverageInfo> => {
  const m: Record<string, any> = {};
  for (const s of summaries) {
    m[s.subscriptionId] = {
      subscriptionId: s.subscriptionId,
      price: s.subscriptionPrice,
      totalPaid: s.totalPaid,          // encaissé (completed)
      paymentsCount: s.paymentsCount,
      isInstallment: s.paymentsCount > 1,
      state: s.coverageState,
      coveragePct: Math.round(Number(s.coveragePct ?? 0)),
      pendingAmount: s.pendingAmount,  // saisi mais pas encaissé
      pendingCount: s.pendingCount,
    };
  }
  return m;
};

/** Filtre de recherche multi-champs (tokens en ET, insensible à la casse). */
export const filterBySearch = (rows: TransactionData[], query: string): TransactionData[] => {
  const q = (query || "").toLowerCase().trim();
  if (!q) return rows;
  const tokens = q.split(/\s+/).filter(Boolean);

  return rows.filter((row) => {
    const hay = [
      row.client,
      row.description, // type d’abonnement
      String((row as any).subscriptionId ?? ""),
      String((row as any).userId ?? ""),
      row.status,
      row.paymentMethod,
      row.date,
      (row as any).paymentsCount,
    ]
      .join(" ")
      .toLowerCase();
    return tokens.every((tok) => hay.includes(tok));
  });
};

/**
 * Filtres rapides : « late » (fin d'abonnement passée ET non couvert) et
 * filtres de couverture (unpaid/partial/paid/overpaid).
 * `today` injecté (yyyy-MM-dd) pour la testabilité.
 */
export const applyQuickFilter = (
  rows: TransactionData[],
  quickFilter: QuickFilter,
  summaries: SubscriptionPaymentSummary[],
  today: string
): TransactionData[] => {
  let out = [...rows];

  // late: abonnement dont la date de fin est passée ET (price > totalPaid) ET price>0
  if (quickFilter === "late") {
    const byId: Record<string, SubscriptionPaymentSummary> = {};
    for (const s of summaries) byId[s.subscriptionId] = s;

    out = out.filter((r) => {
      const sid = (r as any).subscriptionId as string;
      const s = byId[sid];
      if (!s) return false;
      const isUncovered = s.subscriptionPrice > s.totalPaid && s.subscriptionPrice > 0;
      return Boolean(s.endDate && s.endDate < today && isUncovered);
    });
  }

  // pending : au moins un paiement saisi non encaissé. Superposé aux états de
  // couverture (cohérent avec computeKpis : une ligne « payée » avec un
  // paiement pending apparaît ici aussi) ; lignes 0/0 toujours ignorées.
  if (quickFilter === "pending") {
    const byId: Record<string, SubscriptionPaymentSummary> = {};
    for (const s of summaries) byId[s.subscriptionId] = s;

    out = out.filter((r) => {
      const sid = (r as any).subscriptionId as string;
      const s = byId[sid];
      if (!s) return false;
      if (s.subscriptionPrice === 0 && s.totalPaid === 0) return false;
      return s.pendingCount > 0;
    });
  }

  // coverage: unpaid / partial / paid / overpaid
  if (quickFilter && ["unpaid", "partial", "paid", "overpaid"].includes(quickFilter)) {
    const byId: Record<string, SubscriptionPaymentSummary> = {};
    for (const s of summaries) byId[s.subscriptionId] = s;

    out = out.filter((r) => {
      const sid = (r as any).subscriptionId as string;
      const s = byId[sid];
      if (!s) return false;
      if (s.subscriptionPrice === 0 && s.totalPaid === 0) return false; // ignorer
      return s.coverageState === quickFilter;
    });
  }

  return out;
};
